
import React, { useState, useEffect, useRef } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";

interface ResendCodeButtonProps {
  token: string | null;
}

export const ResendCodeButton = ({ token }: ResendCodeButtonProps) => {
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const timerRef = useRef<number | null>(null);
  const { toast } = useToast();
  
  useEffect(() => {
    if (countdown > 0) {
      timerRef.current = window.setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [countdown]);

  const handleResendCode = async () => {
    if (countdown > 0 || resendLoading) return;
    
    setResendLoading(true);
    
    try {
      const response = await fetch("https://api.discoveryconciergeservices.com/api/v1/disco-auth/mobile-resend-verification/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": token ? `Token ${token}` : "", 
        },
      });
      
      // Check if response is ok before trying to parse JSON
      if (!response.ok) {
        // Check content type to determine how to handle the error
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          throw new Error(errorData.detail || "Failed to resend code");
        } else {
          // Handle non-JSON responses with status and statusText
          throw new Error(`Server error: ${response.status} ${response.statusText}`);
        }
      }
      
      // Only try to parse JSON if we know it's JSON
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        await response.json(); // We don't need the data, but parsing it to confirm it's valid
      }
      
      toast({
        title: "Code resent",
        description: "A new verification code has been sent to your phone",
      });
      
      setCountdown(30);
    } catch (error) {
      console.error("Resend code error:", error);
      toast({
        title: "Failed to resend code",
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive",
      });
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="mt-6 text-center">
      <Button
        type="button"
        variant="ghost"
        onClick={handleResendCode}
        disabled={countdown > 0 || resendLoading}
        className="text-sm text-primary hover:underline disabled:opacity-50"
      >
        {resendLoading 
          ? "Sending..." 
          : countdown > 0 
            ? `Resend code in ${countdown}s` 
            : "Didn't receive code? Resend"}
      </Button>
    </div>
  );
};
